import React, { useEffect, useRef } from 'react';
import { Content, Header, SlideContent, SliderControls, Wrapper } from './personal-catalog.styles';
import { AllSpecialSearch, ArrowsChevronLeft, ArrowsChevronRight } from '@vtbhome/juicyfront/indexIcon';
import { Button } from '@vtbhome/juicyfront';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { ROUTE_ADMIN_CATALOG } from '../../../../shared/config/routes/routes';
import { useProductsListSharedQuery } from '@entities/product/libs/hooks/use-products-list-shared-query/use-products-list-shared-query';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { DEFAULT_PRODUCTS_LIST_PARAMETERS } from '@entities/product/config/store-constants';
import { CardProductPreview } from '@entities/product/ui/card-product-preview/card-product-preview';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { usePrefetchProduct } from '@entities/product/api/product-api';
import { usePrefetchOrder } from '@entities/order/api/order-api';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { listOrdersInProductParameters } from '@shared/lib/utils/list-orders-in-product-parameters/list-orders-in-product-parameters';
import { useRedirect } from '@shared/lib/hooks/use-redirect/use-redirect';

export const PersonalCatalog: React.FC = () => {
    const redirect = useRedirect();
    const user = useCurrentUserSelector();
    const swiperRef = useRef<SwiperClass | null>(null);
    const [getProducts, getProductsResult] = useProductsListSharedQuery(DEFAULT_PRODUCTS_LIST_PARAMETERS);
    const productsError = getQueryError(getProductsResult);
    const products = getProductsResult.data?.items;
    const productsLength = products?.length || 0;

    const prefetchProduct = usePrefetchProduct('productShortInfo', { ifOlderThan: 300 });
    const prefetchOrder = usePrefetchOrder('ordersList', { ifOlderThan: 300 });

    const handlePrefetch = (productId: number) => () => {
        prefetchProduct(productId);
        prefetchOrder(
            listOrdersInProductParameters({
                productId,
                user,
            })
        );
    };

    const handleNextSlide = () => {
        const swiper = swiperRef.current;
        if (!swiper) {
            return;
        }
        swiper.slideNext();
    };

    const handlePrevSlide = () => {
        const swiper = swiperRef.current;
        if (!swiper) {
            return;
        }
        swiper.slidePrev();
    };

    useEffect(() => {
        getProducts(DEFAULT_PRODUCTS_LIST_PARAMETERS);
    }, []);

    return (
        <Wrapper>
            <Header>
                <Button
                    buttonType='secondary'
                    startAdornment={<AllSpecialSearch />}
                    onClick={() => redirect({ url: ROUTE_ADMIN_CATALOG })}>
                    Открыть полный каталог
                </Button>
                <SliderControls>
                    <Button
                        disabled={productsLength <= 2}
                        buttonType='secondary'
                        startAdornment={<ArrowsChevronLeft />}
                        onClick={handlePrevSlide}
                    />
                    <Button
                        disabled={productsLength <= 2}
                        buttonType='secondary'
                        startAdornment={<ArrowsChevronRight />}
                        onClick={handleNextSlide}
                    />
                </SliderControls>
            </Header>
            <Content>
                <NetworkStateNew
                    isLoading={
                        getProductsResult.isFetching || getProductsResult.isLoading || getProductsResult.isUninitialized
                    }
                    isError={Boolean(productsError)}
                    isInitialized={Boolean(getProductsResult.data)}
                    hasData={Boolean(productsLength)}
                    isFiltered={Boolean(getProductsResult.data?.search)}
                    errorProps={{
                        title: 'Ошибка загрузки продуктов',
                        description: productsError?.message,
                    }}
                    emptyProps={{
                        title: 'Продукты отсутствуют',
                    }}
                    emptyFilteredProps={{
                        title: 'Продукты не найдены',
                    }}
                    initialLoaderProps={{
                        style: { height: 278 },
                    }}>
                    <Swiper
                        spaceBetween={20}
                        slidesPerView={2}
                        onSwiper={(swiper) => (swiperRef.current = swiper)}
                        loop>
                        {products?.map((product) => (
                            <SwiperSlide key={product.id}>
                                <SlideContent>
                                    <CardProductPreview
                                        product={product}
                                        isShowBorder
                                        onHoverPrefetch={handlePrefetch(product.id)}
                                    />
                                </SlideContent>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </NetworkStateNew>
            </Content>
        </Wrapper>
    );
};
